/** How to play stages, continued: amoeba and magic wall, the Deep, scoring, controls. Pure. */

import type { SpriteName } from '../../render/sprites';
import type { Actor, Label, Stage } from './helpStages';
import { seg } from './helpStages';

const C = 16;

function at(sprite: SpriteName, col: number, row: number, frame = 0): Actor {
  return { sprite, frame, x: Math.round(col * C), y: Math.round(row * C) };
}

function label(text: string, col: number, y: number, colour = 1): Label {
  return { text, x: Math.round(col * C), y, colour };
}

const AMOEBA_ORDER = [[2, 2], [2, 1], [3, 2], [1, 2], [3, 1], [1, 1], [4, 2], [0, 2], [4, 1], [0, 1]] as const;

/** Amoeba spreading through dirt; a boulder dropping through a magic wall comes out a diamond. */
export function amoebaStage(t: number, anim: number): Stage {
  const actors: Actor[] = [];
  const k = t % 5000;
  const grown = Math.min(AMOEBA_ORDER.length, 1 + Math.floor(k / 420));
  for (let c = 0; c <= 4; c++) for (let r = 1; r <= 2; r++) actors.push(at('dirt', c, r));
  AMOEBA_ORDER.slice(0, grown).forEach(([c, r], i) => actors.push(at('amoeba', c, r, (anim >> 1) + i)));
  // The magic wall: a row at 2; a boulder falls onto it, a diamond drops out below.
  const m = t % 2500;
  const active = m > 500 && m < 2200;
  for (let c = 8; c <= 12; c++) actors.push(at(active ? 'magicWall' : 'brick', c, 2, (anim >> 1) + c));
  if (m < 500) actors.push(at('boulder', 10, seg(m, 0, 500)));
  else if (m < 1100) actors.push(at('diamond', 10, 2 + seg(m, 500, 1100) * 1.2, anim >> 1));
  return { actors, labels: [label('AMOEBA', 2.5, 70, 13), label('MAGIC WALL', 10.5, 70, 13)] };
}

/** The Deep's own: expanding wall, slime, a key and its door, a clock. */
export function deepStage(t: number, anim: number): Stage {
  const actors: Actor[] = [];
  const k = t % 4000;
  // Expanding wall grows sideways into space.
  const grow = Math.floor(seg(k, 400, 1600) * 2 + 0.001);
  for (let d = -grow; d <= grow; d++) actors.push(at('expandingWall', 2 + d, 2));
  // Slime: a boulder waits on it, then seeps through and falls out below.
  actors.push(at('boulder', 6, 1 + seg(k, 1500, 2300) * 2));
  actors.push(at('slime', 6, 2, anim >> 1));
  // Key and door: the key goes, the door opens.
  const taken = k > 2200;
  if (!taken) actors.push(at('key0', 9, 2));
  actors.push(at(taken ? (k < 2500 ? 'doorOpening' : 'space') : 'door0', 10, 2));
  actors.push(at('clock', 13, 2, anim >> 3));
  const labels = [
    label('WALL', 2.5, 70, 13), label('SLIME', 6.5, 70, 13), label('KEYS', 10, 70, 13), label('CLOCK', 13.5, 70, 13),
  ];
  return { actors, labels };
}

/** What scores, with the numbers. */
export function scoreStage(t: number, anim: number): Stage {
  const lives = 3 + (Math.floor(t / 1500) % 2);
  const line = (text: string, y: number, colour = 1): Label => ({ text, x: 28, y, colour, left: true });
  return {
    actors: [at('diamond', 0.25, 0.25, anim >> 1), at('clock', 0.25, 1.9, anim >> 3), at('digbyStand', 0.25, 3.5)],
    labels: [
      line('DIAMONDS SCORE THEIR VALUE', 4),
      line('MORE ONCE THE EXIT OPENS', 13, 12),
      line('TIME LEFT BECOMES POINTS', 34),
      line(`A NEW MAN EVERY 500 ${'♥'.repeat(lives)}`, 60),
    ],
  };
}

/** The keys in use, from the player's bindings. */
export function controlsStage(keys: { move: string; move2: string; grab: string; pause: string; pad: string }): Stage {
  const name = (text: string, y: number): Label => ({ text, x: 8, y, colour: 8, left: true });
  const key = (text: string, y: number): Label => ({ text, x: 64, y, colour: 3, left: true });
  return {
    actors: [],
    labels: [
      name('MOVE', 4), key(keys.move, 4), key(keys.move2, 14),
      name('GRAB', 30), key(keys.grab, 30),
      name('PAUSE', 46), key(keys.pause, 46),
      name('PAD', 62), key(keys.pad, 62),
    ],
  };
}
